import { Loader2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import type { BadgeProps } from '@/components/ui/badge';
import type { ProcessingStatus, ScreenshotStatus } from '@/types/api';

type Variant = NonNullable<BadgeProps['variant']>;

const PROCESSING_META: Record<ProcessingStatus, { label: string; variant: Variant }> = {
  RESERVED: { label: 'Queued', variant: 'muted' },
  ENRICHING: { label: 'Enriching', variant: 'secondary' },
  ENRICHED: { label: 'Enriched', variant: 'secondary' },
  SCREENSHOT_PENDING: { label: 'Screenshot pending', variant: 'secondary' },
  COMPLETE: { label: 'Complete', variant: 'success' },
  FAILED: { label: 'Failed', variant: 'destructive' },
  DUPLICATE: { label: 'Duplicate', variant: 'muted' },
  BLOCKED: { label: 'Blocked', variant: 'warning' },
};

const SCREENSHOT_META: Record<ScreenshotStatus, { label: string; variant: Variant }> = {
  PENDING: { label: 'Screenshot pending', variant: 'muted' },
  PROCESSING: { label: 'Screenshot in progress', variant: 'secondary' },
  COMPLETE: { label: 'Screenshot ready', variant: 'success' },
  FAILED: { label: 'Screenshot failed', variant: 'destructive' },
  BLOCKED: { label: 'Screenshot blocked', variant: 'warning' },
};

/** Marks a badge whose status a worker is actively moving on — see useLinkProgress. */
function BusySpinner({ busy }: { busy: boolean | undefined }) {
  return busy ? <Loader2 aria-hidden="true" className="h-3 w-3 animate-spin" /> : null;
}

export function ProcessingStatusBadge({
  status,
  busy,
}: {
  status: ProcessingStatus;
  busy?: boolean;
}) {
  const meta = PROCESSING_META[status];
  return (
    <Badge
      variant={busy ? 'secondary' : meta.variant}
      title={`Processing status: ${status}`}
      aria-busy={busy || undefined}
    >
      <BusySpinner busy={busy} />
      {meta.label}
    </Badge>
  );
}

export function ScreenshotStatusBadge({
  status,
  busy,
}: {
  status: ScreenshotStatus;
  busy?: boolean;
}) {
  const meta = SCREENSHOT_META[status];
  return (
    <Badge
      variant={busy ? 'secondary' : meta.variant}
      title={`Screenshot status: ${status}`}
      aria-busy={busy || undefined}
    >
      <BusySpinner busy={busy} />
      {meta.label}
    </Badge>
  );
}

export function processingStatusLabel(status: ProcessingStatus): string {
  return PROCESSING_META[status].label;
}
